import {
  Content,
  PageWithHeader,
  TableColumn,
} from '@backstage/core-components';
import {
  attachComponentData,
  configApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  EntityListProvider,
  entityRouteRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import {
  DocsTableRow,
  EntityListDocsTable,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { parseEntityRef, RELATION_PART_OF } from '@backstage/catalog-model';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { getExplorerTableSx } from '../entity-explorer/shared';
import { shellMetrics } from '../../theme/tokens';
import { DocsExplorerControls } from './explorer-page/DocsExplorerControls';
import { useDocsExplorerState } from './explorer-page/useDocsExplorerState';

const DocsExplorerTable = () => {
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();

  const systemColumn: TableColumn<DocsTableRow> = {
    title: 'System',
    field: 'entity.spec.system',
    width: '15%',
    render: ({ entity }) => {
      const system = entity.spec?.system as string | undefined;
      const systemRef = system
        ? `system:${entity.metadata.namespace ?? 'default'}/${system}`
        : entity.relations?.find(
            r =>
              r.type === RELATION_PART_OF && r.targetRef.startsWith('system:'),
          )?.targetRef;
      if (!systemRef) return '—';
      const parsed = parseEntityRef(systemRef);
      return (
        <Link component={RouterLink} to={catalogEntityRoute(parsed)}>
          {parsed.name}
        </Link>
      );
    },
  };

  const columns: TableColumn<DocsTableRow>[] = [
    { ...EntityListDocsTable.columns.createTitleColumn(), width: '50%' },
    { ...EntityListDocsTable.columns.createOwnerColumn(), width: '18%' },
    { ...systemColumn, width: '18%' },
  ];

  const actions = [
    EntityListDocsTable.actions.createStarEntityAction(
      isStarredEntity,
      toggleStarredEntity,
    ),
  ];

  return (
    <EntityListDocsTable
      columns={columns}
      actions={actions}
      options={{
        actionsCellStyle: { textAlign: 'center' },
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        padding: 'dense',
        paginationPosition: 'bottom',
        search: false,
        toolbar: false,
      }}
    />
  );
};

const DocsExplorerContent = () => {
  const theme = useTheme();
  const { searchText, setSearchText } = useDocsExplorerState();

  return (
    <Stack spacing={2}>
      <DocsExplorerControls
        searchText={searchText}
        setSearchText={setSearchText}
      />

      <Box
        sx={{
          ...getExplorerTableSx(theme, { linkColumnIndices: [1, 2] }),
          '& td:last-child > div': { justifyContent: 'center' },
          '& th:last-child': { textAlign: 'center' },
        }}
      >
        <DocsExplorerTable />
      </Box>
    </Stack>
  );
};

export const CustomDocsPage = () => {
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';

  return (
    <PageWithHeader
      subtitle={`Documentation available in ${orgName}`}
      title="Documentation"
      themeId="documentation"
    >
      <Content>
        <Box
          sx={{
            boxSizing: 'border-box',
            maxWidth: 'none',
            px: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Stack spacing={shellMetrics.sectionGap / 8}>
            <EntityListProvider pagination={{ limit: 10, mode: 'offset' }}>
              <DocsExplorerContent />
            </EntityListProvider>
          </Stack>
        </Box>
      </Content>
    </PageWithHeader>
  );
};

attachComponentData(
  CustomDocsPage,
  'core.mountPoint',
  techdocsPlugin.routes.root,
);
